import { action, computed, makeObservable, observable } from 'mobx';
import { TOption } from 'src/features/well-form/types';
import { debounce } from 'src/shared/utils/debounce';
import { TPlanVersion } from 'src/store/comparison/types';
import { Directories } from 'src/store/directories/directories.store';
import { WellPadPreparationService } from 'src/store/well-pad-preparation';
import { PadPreparation } from 'src/store/well-pad-preparation/entities';

import { PAD_DRILLING_STATUS } from './consts';
import { WellPadPreparationFilterStore } from './well-pad-preparation-filter.store';

export type TPadDrillingStatus = keyof typeof PAD_DRILLING_STATUS;

interface IPlanVersionService {
  publicDraft: TPlanVersion | null;
  currentVersion: TPlanVersion | null;
  isDraftOfPublicDraftVersion(version: number | TPlanVersion): boolean;
}

export class WellPadPreparationStore {
  private readonly planVersion: IPlanVersionService;
  private readonly wellPadPreparationService: WellPadPreparationService;
  private readonly directoriesService: Directories;

  readonly filterStore: WellPadPreparationFilterStore = new WellPadPreparationFilterStore();

  @observable currentPadStatusToDisplay: TPadDrillingStatus = 'planned';
  @observable searchValue: string = '';
  @observable isFilterModalOpened = false;
  @observable editingPadId: number | null = null;

  constructor(
    directoriesService: Directories,
    planVersionsService: IPlanVersionService,
    wellPadPreparationService: WellPadPreparationService,
  ) {
    this.planVersion = planVersionsService;
    this.wellPadPreparationService = wellPadPreparationService;
    this.directoriesService = directoriesService;

    makeObservable(this);
  }

  @computed
  private get getSearchFilterFn(): (pad: PadPreparation) => boolean {
    const searchValue = this.searchValue;

    return (pad) => {
      const {
        constantValues: { padName, blockName, fieldName },
      } = pad;

      return padName.includes(searchValue) || blockName.includes(searchValue) || fieldName.includes(searchValue);
    };
  }

  @computed
  private get getFilterFnByPadStatus(): (pad: PadPreparation) => boolean {
    const getPadStatus = (pad: PadPreparation): TPadDrillingStatus => {
      if (!pad.rigSiteEvents.length || (pad.rigSiteEvents.length === 1 && pad.rigSiteEvents[0].id === 'blank')) {
        return 'unplanned';
      }

      if (!!pad.values.isChanged) {
        return 'updated';
      }

      if (!!pad.constantValues.drilled) {
        return 'drilled';
      }

      return 'planned';
    };

    return (pad) => {
      return this.currentPadStatusToDisplay === getPadStatus(pad);
    };
  }

  private _updateSearchValue(searchValue: string): void {
    this.searchValue = searchValue;
  }

  @computed
  private get sortedPadPreparations(): PadPreparation[] {
    return [...this.wellPadPreparationService.pads].sort((a, b) => {
      const { blockName: blockNameA, fieldName: fieldNameA, padName: padNameA } = a.constantValues;
      const { blockName: blockNameB, fieldName: fieldNameB, padName: padNameB } = b.constantValues;

      const blockCompRes = blockNameA.localeCompare(blockNameB);
      const fieldCompRes = fieldNameA.localeCompare(fieldNameB) / 10;
      const padNameCompRes = padNameA.localeCompare(padNameB) / 100;

      return blockCompRes + fieldCompRes + padNameCompRes;
    });
  }

  @computed
  get noResultReason(): 'noData' | 'noDataByFilters' | null {
    if (this.pads.length) {
      return null;
    }

    if (!this.wellPadPreparationService.pads.length) {
      return 'noData';
    }

    return 'noDataByFilters';
  }

  @computed
  get padTypeOptions(): TOption<number>[] {
    return (
      this.directoriesService
        .getObject('Common_PadType')
        ?.map((d) => ({ label: d.data['name'].toString(), value: d.id as number })) ?? []
    );
  }

  @computed
  get pads(): PadPreparation[] {
    return this.sortedPadPreparations.filter(
      (pad) => this.getFilterFnByPadStatus(pad) && this.getSearchFilterFn(pad) && this.filterStore.filterFn(pad),
    );
  }

  @computed
  get isEditModeAvailable(): boolean {
    const currentVersion = this.planVersion.currentVersion;

    if (!currentVersion) {
      return false;
    }

    return currentVersion === this.planVersion.publicDraft;
  }

  @computed
  get isLoading(): boolean {
    return this.wellPadPreparationService.isDataFetching || !this.wellPadPreparationService.isInitialized;
  }

  @action.bound
  setEditingPadId(id: number | null): void {
    this.editingPadId = id;
  }

  @action.bound
  setIsFilterModalOpen(is: boolean): void {
    this.isFilterModalOpened = is;
  }

  @action.bound
  toggleFilterModal(): void {
    this.setIsFilterModalOpen(!this.isFilterModalOpened);
  }

  @action.bound
  setPadStatusToDisplay(status: TPadDrillingStatus): void {
    this.currentPadStatusToDisplay = status;
  }

  setSearchValue = debounce(action(this._updateSearchValue.bind(this)), 300);
}
